import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import useCompanyEquipmentBlock from "../../hooks/useEquipmentBlock";
import useCompanyEquipmentUnblock from "../../hooks/useEquipmentUnblock";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { equipmentBlockSchema, EquipmentBlockValidationSchema } from "../../schemas/blockEquipment";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { EquipmentData } from "../../types/equipments.type";
import { formatDateTime } from "@/common/utils/formatters";
import { DateInput } from "@/common/components/DateInput/DateInput";
import SelectController from "@/common/components/SelectController";
import { periodWithHalfMinutesOptions } from "@/common/constants/periodOptionsWithHalfMinutes.constant";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import TextArea from "@/common/components/TextArea";

const BlockUnblockEquipment = ({ equipment }: { equipment: EquipmentData; }) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [blockModalIsOpen, setBlockModalIsOpen] = useState(false);
  const [unblockModalIsOpen, setUnblockModalIsOpen] = useState(false);

  const {
    mutate: blockEquipment,
    isPending: blockEquipmentIsPending,
  } = useCompanyEquipmentBlock({
    equipmentId: equipment.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipment.id] });
      reset();
      setBlockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: setError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const {
    mutate: unblockEquipment,
    isPending: unblockEquipmentIsPending,
  } = useCompanyEquipmentUnblock({
    equipmentId: equipment.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipment.id] });
      setUnblockModalIsOpen(false);
    },
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isBlocked = equipment.status === 'blocked';
  const isDisabled = equipment.status === 'disabled';

  const {
    control,
    register,
    setError,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EquipmentBlockValidationSchema>({
    resolver: zodResolver(equipmentBlockSchema),
  });

  const onSubmitBlockEquipment = (form: EquipmentBlockValidationSchema) => {
    if (form.scheduled_to_unblock_at?.date && form.scheduled_to_unblock_at?.time?.value) {
      const [day, month, year] = form.scheduled_to_unblock_at?.date.split('/').map(Number);
      const [hours, minutes] = form.scheduled_to_unblock_at?.time.value.split(':').map(Number);
      const scheduledDate = new Date(year, month - 1, day, hours, minutes);

      form.scheduled_to_unblock_at_str = scheduledDate.toISOString();
    }

    blockEquipment(form);
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        {!isDisabled && (
          <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isBlocked && 'bg-[#FCCDCA33]')}>
            <div className="flex items-center gap-x-2">
              <InfoIcon strokeWidth={1} className="text-yellow-600" />

              <div>
                <p className="font-bold text-[10px] leading-3">
                  {!isBlocked ? (
                    'Bloqueio do equipamento impede que todas as transações sejam realizadas.'
                  ) : (
                    'Desbloqueie o equipamento para permitir as transações.'
                  )}
                </p>

                <p className="font-light text-[10px] italic leading-3">
                  {!isBlocked ? (
                    'Caso o equipamento seja bloqueada, nenhuma transação poderá ser feita nele.'
                  ) : (
                    'Se o equipamento for desbloqueada, transações poderão ser feitas.'
                  )}
                </p>
              </div>
            </div>

            {!isBlocked ? (
              <Modal
                Trigger={(
                  <Button type="button" variant="error">
                    Bloquear equipamento
                  </Button>
                )}
                title="Você tem certeza?"
                subtitle="Caso o equipamento seja bloqueada, nenhuma transação poderá ser feita nele."
                titleClass="text-xl"
                subtitleClassName="mt-1 text-white-300"
                showClose={false}
                useCancellableFooter
                isOpen={blockModalIsOpen}
                setIsOpen={setBlockModalIsOpen}
                onConfirm={handleSubmit(onSubmitBlockEquipment)}
                confirmButtonIsLoading={unblockEquipmentIsPending}
                cancelButtonClassName="bg-white-200 rounded-es-lg"
                bodyClassName="text-white-800 rounded-ee-lg"
                body={(
                  <form className="flex flex-col gap-6">
                    <TextArea
                      label="Motivo"
                      markAsRequired
                      {...register('block_reason')}
                      variant={errors.block_reason?.message ? 'error' : 'default'}
                      errorMessage={errors.block_reason?.message || undefined}
                      disabled={blockEquipmentIsPending}
                    />

                    <div className="flex flex-col gap-2.5">
                      <p className="text-base font-bold text-white-800 opacity-80">
                        Agendar desbloqueio automático (opcional)
                      </p>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-start gap-3">
                          <DateInput
                            label="Data"
                            control={control}
                            fieldName="scheduled_to_unblock_at.date"
                            errors={errors}
                            className="w-full"
                            errorMessage={errors.scheduled_to_unblock_at?.date?.message || undefined}
                            disabled={blockEquipmentIsPending}
                          />

                          <SelectController
                            label="Hora"
                            control={control}
                            name="scheduled_to_unblock_at.time"
                            className="w-full"
                            options={periodWithHalfMinutesOptions}
                            isClearable
                            errorMessage={errors.scheduled_to_unblock_at?.time?.message || undefined}
                            isDisabled={blockEquipmentIsPending}
                          />
                        </div>

                        {(errors.scheduled_to_unblock_at?.message) && (
                          <ErrorMessage
                            name="scheduled_to_unblock_at"
                            message={errors.scheduled_to_unblock_at?.message || ''}
                          />
                        )}
                      </div>
                    </div>
                  </form>
                )}
              />
            ) : (
              <div className="flex items-center gap-x-4">
                {equipment.scheduled_to_unblock_at && (
                  <p className="text-xs text-white-800">
                    Desbloqueio automático agendado para: {formatDateTime(new Date(equipment.scheduled_to_unblock_at))}.
                  </p>
                )}

                <Modal
                  Trigger={(
                    <Button type="button">
                      Desbloquear equipamento
                    </Button>
                  )}
                  title="Você tem certeza?"
                  subtitle="Se o equipamento for desbloqueada, transações poderão ser feitas."
                  subtitleClassName="mt-2 text-white-300"
                  confirmButtonClassName="bg-blue-700"
                  showClose={false}
                  useCancellableFooter
                  isOpen={unblockModalIsOpen}
                  setIsOpen={setUnblockModalIsOpen}
                  onConfirm={() => unblockEquipment()}
                  confirmButtonIsLoading={unblockEquipmentIsPending}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default BlockUnblockEquipment;
